/**
 * useClaimsQueue — owns the Billing Management "Claims Queue" data.
 *
 * Extracted from App.js (Feb 2026) as the smallest-safe step in the
 * App.js monolith decomposition.  Just the data layer; all the
 * dialog/handler/JSX call sites stay in App.js and consume
 * `fetchClaimsQueue()` exactly as before — they don't know they're
 * talking to a hook.
 *
 * Scope (intentionally narrow):
 *   • State: claimsQueue, claimsQueueRefreshKey, claimsSortColumn,
 *     claimsSortDirection, loadingClaims (and their setters)
 *   • Action: fetchClaimsQueue()
 *
 * Out of scope (stays in App.js):
 *   • Selected-claim dialog state (selectedClaim, showClaimDetailDialog,
 *     etc.) — too entangled with specific dialog flows.
 *   • Bulk-selection arrays (selectedClaimsForPayment etc.) — same.
 *   • All claim handlers (submit/void/resubmit/markBilled) — they
 *     compose multiple unrelated state slices and live near their JSX.
 *
 * Permission gate is preserved exactly as the inline implementation:
 * billing managers + super-admins always run; billers run too;
 * everyone else short-circuits.
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * @param {object} opts
 * @param {object} opts.user                   current user (permission gate)
 * @param {Function} opts.isSuperAdmin         (user) => boolean
 * @param {string|string[]} opts.billingProviderFilter   filter from App.js
 * @param {string} opts.billingStatusFilter    filter from App.js ('all' / 'ready_for_billing' / etc.)
 */
export function useClaimsQueue({ user, isSuperAdmin, billingProviderFilter, billingStatusFilter }) {
  const [claimsQueue, setClaimsQueue] = useState([]);
  const [claimsQueueRefreshKey, setClaimsQueueRefreshKey] = useState(0);
  const [claimsSortColumn, setClaimsSortColumn] = useState('date_of_service');
  const [claimsSortDirection, setClaimsSortDirection] = useState('desc');
  const [loadingClaims, setLoadingClaims] = useState(false);

  const fetchClaimsQueue = useCallback(async () => {
    // Same permission gate as the inline original — billing managers
    // and super-admins always pass; billers also allowed; everyone
    // else returns silently so we don't 403-spam the console.
    if (!user?.is_billing_manager && !isSuperAdmin?.(user)) {
      if (!user?.is_biller) return;
    }

    setLoadingClaims(true);
    try {
      const params = new URLSearchParams();
      // Only narrow by provider when exactly ONE is selected — matches
      // the queue endpoint's `provider_id` (singular) contract.
      if (Array.isArray(billingProviderFilter) && billingProviderFilter.length === 1) {
        params.append('provider_id', billingProviderFilter[0]);
      }
      if (billingStatusFilter && billingStatusFilter !== 'all') {
        params.append('status', billingStatusFilter);
      }

      const response = await axios.get(`${API}/claims/queue?${params.toString()}`);
      const data = response?.data || {};
      setClaimsQueue(data.claims || []);
      // Bump refresh key so memoized children re-render after the mutation.
      setClaimsQueueRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Failed to fetch claims queue:', error);
      toast.error('Failed to load billing queue');
    } finally {
      setLoadingClaims(false);
    }
  }, [user, isSuperAdmin, billingProviderFilter, billingStatusFilter]);

  return {
    // state
    claimsQueue,
    claimsQueueRefreshKey,
    claimsSortColumn,
    claimsSortDirection,
    loadingClaims,
    // setters (App.js sort UI mutates these directly)
    setClaimsQueue,
    setClaimsSortColumn,
    setClaimsSortDirection,
    setLoadingClaims,
    // action
    fetchClaimsQueue,
  };
}
